import { DownloadOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, Col, Input, Row, Select, Space, Table, Tag } from 'antd';
import { useEffect, useState } from 'react';

import { axiosClient } from '@/api/axiosClient';
import { colLabel, totalLabel } from '@/modules/report/columnLabels';

interface ReportDefinition {
  id: string;
  name: string;
  group: string;
  columns: string[];
  filters: string[];
  implemented: boolean;
}

interface ReportResult {
  definition: ReportDefinition;
  rows: Record<string, unknown>[];
  totals?: Record<string, unknown>;
}

// Phần IV — 17 reports module. Catalogue picker on the left, run + table
// + export on the right. Filter inputs render dynamically off the report
// definition's `filters` slice.
//
// NB: axiosClient.get returns the full BE envelope { status, message, data }
// (see src/api/axiosClient.ts::onResponse). useCallApi unwraps `.data` but
// pages that hit axiosClient directly need to do it themselves — accessing
// the payload as if it were the inner type throws "c.find is not a function"
// at runtime.
const ReportPage = () => {
  const [selectedId, setSelectedId] = useState<string>('');
  const [filterValues, setFilterValues] = useState<Record<string, string>>({});
  // QC #38 — open with runKey=1 so the first report auto-runs once the
  // catalogue loads. Previous behavior: blank page until user clicked
  // "Chạy", which QC interpreted as "tab Báo cáo no data".
  const [runKey, setRunKey] = useState(1);

  const { data: catalogueResp } = useQuery(
    ['report-catalogue'],
    () => axiosClient.get<ReportDefinition[]>('/report/catalogue'),
    { refetchOnWindowFocus: false, staleTime: Infinity }
  );
  const catalogue =
    (catalogueResp as unknown as { data?: ReportDefinition[] })?.data ?? [];

  // QC #38 — auto-select RP-01 when the catalogue lands so the page never
  // shows a blank left pane. User can still pick a different report.
  useEffect(() => {
    if (!selectedId && catalogue.length > 0) {
      setSelectedId(catalogue[0].id);
    }
  }, [catalogue, selectedId]);

  const selectedDef = catalogue.find((d) => d.id === selectedId);

  const { data: resultResp, isFetching } = useQuery(
    ['report-run', selectedId, runKey],
    () => {
      const qs = new URLSearchParams();
      Object.entries(filterValues).forEach(([k, v]) => v && qs.set(k, v));

      return axiosClient.get<ReportResult>(`/report/${selectedId}?${qs.toString()}`);
    },
    { enabled: !!selectedId && runKey > 0, refetchOnWindowFocus: false }
  );
  const result = (resultResp as unknown as { data?: ReportResult })?.data;

  function handleExport() {
    if (!selectedId) return;
    const qs = new URLSearchParams();
    Object.entries(filterValues).forEach(([k, v]) => v && qs.set(k, v));
    const url = `${import.meta.env.VITE_BASE_API_URL}/report/${selectedId}/export?${qs.toString()}`;
    window.open(url, '_blank');
  }

  return (
    <Row gutter={16}>
      <Col span={6}>
        <Card title="17 báo cáo (Phần IV)">
          <Select
            onChange={(v) => {
              setSelectedId(v);
              setFilterValues({});
              setRunKey(0);
            }}
            options={catalogue.map((d) => ({
              label: (
                <span>
                  <Tag color={d.implemented ? 'green' : 'orange'}>{d.id}</Tag>
                  {d.name}
                </span>
              ),
              value: d.id,
            }))}
            placeholder="Chọn báo cáo..."
            style={{ width: '100%' }}
            value={selectedId || undefined}
          />
          {selectedDef && !selectedDef.implemented && (
            <div className="mt-3 text-warning">
              Báo cáo này hiện ở trạng thái scaffold — chưa có truy vấn dữ liệu thực.
            </div>
          )}
        </Card>
      </Col>

      <Col span={18}>
        {selectedDef && (
          <Card
            extra={
              <Space>
                <Button
                  icon={<ReloadOutlined rev="" />}
                  loading={isFetching}
                  onClick={() => setRunKey((k) => k + 1)}
                  type="primary"
                >
                  Chạy
                </Button>
                <Button icon={<DownloadOutlined rev="" />} onClick={handleExport}>
                  Export CSV
                </Button>
              </Space>
            }
            title={`${selectedDef.id} — ${selectedDef.name}`}
          >
            <Space className="mb-3" wrap>
              {selectedDef.filters.map((f) => (
                <Input
                  key={f}
                  onChange={(e) => setFilterValues({ ...filterValues, [f]: e.target.value })}
                  // QC UX — show Vietnamese label as placeholder; falls
                  // back to raw code if not in columnLabels map.
                  placeholder={colLabel(f)}
                  style={{ width: 180 }}
                  value={filterValues[f] ?? ''}
                />
              ))}
            </Space>

            <Table
              columns={selectedDef.columns.map((c) => ({
                dataIndex: c,
                ellipsis: true,
                key: c,
                // QC UX — VN label, falls back to raw code so a new
                // RP column auto-renders without crash.
                title: colLabel(c),
              }))}
              dataSource={(result?.rows || []).map((r, i) => ({
                ...r,
                _key: i,
              }))}
              loading={isFetching}
              pagination={{ pageSize: 20 }}
              rowKey="_key"
              scroll={{ x: 'max-content' }}
              size="small"
            />

            {result?.totals && (
              <div className="mt-3 p-3 bg-light">
                <strong>Tổng:</strong>{' '}
                {Object.entries(result.totals).map(([k, v]) => (
                  <Tag color="blue" key={k}>
                    {totalLabel(k)}: {String(v)}
                  </Tag>
                ))}
              </div>
            )}
          </Card>
        )}
      </Col>
    </Row>
  );
};

export default ReportPage;
